/**
 * Assumptions Hub API client.
 *
 * Talks to the portal proxy (NOT directly to API Gateway). The proxy forwards
 * cross-account to the ASHI assumptions API Gateway with SigV4 signing; the
 * browser only ever sends its portal session cookie. This replaces the
 * standalone Hub's Amplify client (which signed in-browser via a Cognito
 * Identity Pool — the credentials that were compromised).
 *
 * Endpoints (all under the portal's /api):
 *   GET    /api/assumptions            list current assumption files
 *   GET    /api/assumptions/{fileId}   fetch one file's parsed content
 *   POST   /api/assumptions            upload a new file
 *   DELETE /api/assumptions/{fileId}   archive a file
 *   GET    /api/templates              list templates
 *   GET    /api/templates/{fileId}     fetch one template's columns
 *
 * Auth pattern mirrors lib/tableau.ts: fetch with credentials:"include".
 */
import type {
  AssumptionContentResponse,
  DeleteResponse,
  FileListResponse,
  FileTemplateMappings,
  GridRow,
  TemplateContentResponse,
  UploadResponse,
} from "./types";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "";

/**
 * S3 key of the file->template mapping file.
 *
 * The backend stores it at `{TEMPLATES_PREFIX}/file_template.json`
 * (see handler.py _get_file_template_key). With TEMPLATES_PREFIX =
 * "hub-data/templates", that's the key below. It's fetched through the
 * TEMPLATES route (which returns parsed JSON), not the assumptions route.
 *
 * If a future environment changes TEMPLATES_PREFIX, change this to match.
 */
const FILE_TEMPLATE_KEY = "hub-data/templates/file_template.json";

// ---- helpers ---------------------------------------------------------------

function jsonHeaders(): HeadersInit {
  return { "Content-Type": "application/json" };
}

/**
 * base64url-encode an S3 key for use as a path parameter.
 * Matches the original Hub's encodeFileId exactly:
 *   btoa(key).replace(/\+/g,'-').replace(/\//g,'_')
 * The Lambda decodes it back to the raw S3 key.
 */
export function encodeFileId(s3Key: string): string {
  return btoa(s3Key).replace(/\+/g, "-").replace(/\//g, "_");
}

/**
 * Pull a useful error message out of a failed proxy response. The proxy
 * forwards the Lambda's JSON ({error|message}) verbatim; fall back to status.
 */
async function errorFrom(response: Response): Promise<string> {
  try {
    const text = await response.text();
    if (!text) return `HTTP ${response.status}`;
    try {
      const parsed = JSON.parse(text);
      return parsed.error || parsed.message || parsed.detail || text;
    } catch {
      return text;
    }
  } catch {
    return `HTTP ${response.status}`;
  }
}

/** Convert a grid (array of row objects) to a base64-encoded CSV string. */
export function jsonToBase64Csv(rows: GridRow[]): string {
  if (!rows || rows.length === 0) return "";
  const headers = Object.keys(rows[0]);
  const csvRows: string[] = [headers.join(",")];
  for (const row of rows) {
    const values = headers.map((h) => {
      const val = row[h];
      if (val === null || val === undefined) return '""';
      const str = String(val).replace(/"/g, '""');
      return `"${str}"`;
    });
    csvRows.push(values.join(","));
  }
  const csv = csvRows.join("\n");
  // Encode as UTF-8 -> base64 (handles non-ASCII; matches original helper).
  return btoa(unescape(encodeURIComponent(csv)));
}

// ---- assumptions endpoints -------------------------------------------------

/** GET /api/assumptions — list current assumption files. */
export async function listAssumptions(): Promise<FileListResponse> {
  const res = await fetch(`${API_BASE}/api/assumptions`, {
    credentials: "include",
    headers: jsonHeaders(),
  });
  if (!res.ok) throw new Error(await errorFrom(res));
  return res.json();
}

/**
 * GET /api/assumptions/{fileId} — fetch one file's parsed CSV content.
 * @param s3Key full S3 key; encoded to the path param internally.
 */
export async function fetchAssumptionContent(
  s3Key: string,
): Promise<AssumptionContentResponse> {
  const fileId = encodeFileId(s3Key);
  const res = await fetch(`${API_BASE}/api/assumptions/${fileId}`, {
    credentials: "include",
    headers: jsonHeaders(),
  });
  if (!res.ok) throw new Error(await errorFrom(res));
  return res.json();
}

/** POST /api/assumptions — upload a new file (base64 CSV + template metadata). */
export async function uploadAssumption(
  rows: GridRow[],
  fileName: string,
  templateId = "",
  templateVersion = "1.0",
  originalS3Key = "",
): Promise<UploadResponse> {
  const base64Content = jsonToBase64Csv(rows);
  const rowCount = rows ? rows.length : 0;
  const res = await fetch(`${API_BASE}/api/assumptions`, {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders(),
    body: JSON.stringify({
      filename: fileName,
      content: base64Content,
      template_id: templateId,
      template_version: templateVersion,
      row_count: rowCount,
      original_s3_key: originalS3Key,
    }),
  });
  if (!res.ok) throw new Error(await errorFrom(res));
  return res.json();
}

/** DELETE /api/assumptions/{fileId} — archive a file (move current -> archive). */
export async function deleteAssumption(s3Key: string): Promise<DeleteResponse> {
  const fileId = encodeFileId(s3Key);
  const res = await fetch(`${API_BASE}/api/assumptions/${fileId}`, {
    method: "DELETE",
    credentials: "include",
    headers: jsonHeaders(),
  });
  if (!res.ok) throw new Error(await errorFrom(res));
  return res.json();
}

// ---- templates endpoints ---------------------------------------------------

/** GET /api/templates — list templates. */
export async function listTemplates(): Promise<FileListResponse> {
  const res = await fetch(`${API_BASE}/api/templates`, {
    credentials: "include",
    headers: jsonHeaders(),
  });
  if (!res.ok) throw new Error(await errorFrom(res));
  return res.json();
}

/** GET /api/templates/{fileId} — fetch one template's columns. */
export async function fetchTemplateContent(
  s3Key: string,
): Promise<TemplateContentResponse> {
  const fileId = encodeFileId(s3Key);
  const res = await fetch(`${API_BASE}/api/templates/${fileId}`, {
    credentials: "include",
    headers: jsonHeaders(),
  });
  if (!res.ok) throw new Error(await errorFrom(res));
  return res.json();
}

// ---- file -> template mappings ---------------------------------------------

/**
 * Fetch the file->template mapping (file_template.json).
 *
 * The backend stores this as plain JSON under TEMPLATES_PREFIX, so we fetch
 * it through the TEMPLATES route — handler_fetch_template returns the parsed
 * JSON under `data`. (The earlier approach fetched through the assumptions
 * route, which CSV-mangles JSON, and used the wrong key; both are fixed here.)
 *
 * The file's shape is { version, last_updated, mappings: { <s3Key>: {
 * file_name, template_id, row_count, uploaded_at } } }. Mappings are keyed by
 * full S3 key (robust against same filename across year= partitions).
 *
 * If the file doesn't exist yet (fresh environment) the fetch 404s; the caller
 * (useAssumptions) treats a thrown error as "no mappings" and degrades
 * gracefully.
 */
export async function fetchFileTemplateMappings(): Promise<FileTemplateMappings> {
  const result = await fetchTemplateContent(FILE_TEMPLATE_KEY);
  const data = result.data as unknown as FileTemplateMappings | undefined;

  if (data && typeof data === "object" && "mappings" in data) {
    return data;
  }

  throw new Error("Unexpected response format for file_template.json");
}
